import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import FilterBy, {
  FilterBySelectedItems,
  FilterByOptions,
  Option,
} from '../../FilterBy';
import { accountSelector } from '../../../../../shared/store/shared/accountSlice';
import { useGetDealsListQuery } from '../../../../../shared/store/shared/dealsApiSlice';
import { ProductApiModel } from '../../../../../shared/models/ProductModel';
import { AgreementApiModel } from '../../../../../shared/models/DealModel';

export interface UserFilters extends FilterBySelectedItems {
  registrationStatus: string[];
  products: string[];
  dealIds: string[];
}

interface FilterUserProps {
  onFilter: (isFiltering: boolean, selectedFilters: UserFilters) => void;
  show: boolean;
  reFetchData: boolean;
  resetFilter: boolean;
}

const registrationStatusOptions: Option[] = [
  { label: 'Disabled', value: 'Removed' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Registered', value: 'Registered' },
  { label: 'Undeliverable', value: 'Failed' },
];

function getDealOptions(
  deals: AgreementApiModel[],
  filteredProductSkus: string[],
): Option[] {
  const dealsToUse =
    filteredProductSkus.length > 0
      ? deals.filter((d) =>
          d.products?.some((p) => filteredProductSkus.includes(p.sku)),
        )
      : deals;
  return dealsToUse.map((deal) => ({
    label: deal.dealName || deal.id,
    value: deal.id,
  }));
}

function getProductOptions(
  deals: AgreementApiModel[],
  filteredDealIds: string[],
): Record<string, Option> {
  const dealsToUse =
    filteredDealIds.length > 0
      ? deals.filter((d) => filteredDealIds.includes(d.id))
      : deals;

  const products = new Map<string, ProductApiModel>();
  dealsToUse.forEach((deal) => {
    if (deal.products?.length > 0) {
      deal.products.forEach((p) => products.set(p.sku, p));
    }
  });

  const result: Record<string, Option> = {};
  Array.from(products.values())
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((p) => {
      result[`product_${p.sku.replace(/-/g, '_')}`] = {
        value: p.sku,
        label: p.name.length > 0 ? p.name : p.sku,
      };
    });
  return result;
}

export default function FilterUsers({
  onFilter,
  show = false,
  reFetchData = false,
  resetFilter = false,
}: FilterUserProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedDealIds, setSelectedDealIds] = useState<string[]>([]);
  const [selectedProductSkus, setSelectedProductSkus] = useState<string[]>([]);

  const { currentAccountId } = useSelector(accountSelector);
  const [reloadGroups, setReloadGroups] = useState(reFetchData);

  const { data: currentUserDeals, refetch: refetchDeals } =
    useGetDealsListQuery(currentAccountId, {
      skip: !currentAccountId,
    });

  const dealOptions = useMemo(
    () =>
      currentUserDeals?.length
        ? getDealOptions(currentUserDeals, selectedProductSkus)
        : [],
    [currentUserDeals, selectedProductSkus],
  );

  const productOptions = useMemo(
    () =>
      currentUserDeals?.length
        ? getProductOptions(currentUserDeals, selectedDealIds)
        : {},
    [currentUserDeals, selectedDealIds],
  );

  const filterByItems = useMemo<FilterByOptions[]>(
    () => [
      { text: 'Agreements', key: 'dealIds', options: dealOptions },
      { text: 'Products', key: 'products', options: productOptions as any },
      {
        text: 'Registration Status',
        key: 'registrationStatus',
        options: registrationStatusOptions,
      },
    ],
    [dealOptions, productOptions],
  );

  const onFilterChanged = ({
    registrationStatus = [],
    products = [],
    dealIds = [],
  }: FilterBySelectedItems) => {
    const selectedDealValues = dealIds.map((d: Option | string) =>
      typeof d === 'string' ? d : d.value,
    );
    const selectedProductValues = products.map((p: Option | string) =>
      typeof p === 'string' ? p : p.value,
    );

    if (currentUserDeals?.length) {
      // Cross-filter pruning
      const visibleDealIds = new Set(
        getDealOptions(currentUserDeals, selectedProductValues).map(
          (d) => d.value,
        ),
      );
      const prunedDealIds = selectedDealValues.filter((id) =>
        visibleDealIds.has(id),
      );

      const visibleSkus = new Set(
        Object.values(getProductOptions(currentUserDeals, prunedDealIds)).map(
          (p) => p.value,
        ),
      );
      const prunedProductSkus = selectedProductValues.filter((sku) =>
        visibleSkus.has(sku),
      );

      setSelectedDealIds(prunedDealIds);
      setSelectedProductSkus(prunedProductSkus);

      const isFiltering =
        registrationStatus.length > 0 ||
        prunedProductSkus.length > 0 ||
        prunedDealIds.length > 0;
      onFilter(isFiltering, {
        registrationStatus,
        products: prunedProductSkus,
        dealIds: prunedDealIds,
      });
    } else {
      setSelectedDealIds(selectedDealValues);
      setSelectedProductSkus(selectedProductValues);

      const isFiltering =
        registrationStatus.length > 0 ||
        products.length > 0 ||
        dealIds.length > 0;
      onFilter(isFiltering, {
        registrationStatus,
        products: selectedProductValues,
        dealIds: selectedDealValues,
      });
    }
  };

  const defaultSelectedItems = useMemo(
    () => ({
      registrationStatus: searchParams.getAll('statuses') ?? [],
      products: searchParams.getAll('products') ?? [],
      dealIds: searchParams.getAll('dealId') ?? [],
    }),
    [location.search],
  );

  useEffect(() => {
    if (
      reloadGroups &&
      filterByItems &&
      Array.isArray(filterByItems) &&
      filterByItems[1].options &&
      Object.keys(filterByItems[1].options).length > 1
    ) {
      refetchDeals();
      setReloadGroups(false);
    }
  }, [reFetchData]);

  return (
    <div className={`mb-3 ${show ? 'd-block' : 'd-none'}`}>
      <FilterBy
        items={filterByItems}
        onChange={onFilterChanged}
        defaultSelectedItems={defaultSelectedItems}
        resetFilter={resetFilter}
      />
    </div>
  );
}
